<template>
    <PanelPage>
        <template v-slot:title>
            <SmartBreadcrumbs
                type="a"
                :pages="[
                    {
                        name: $tc('messages.payments', 2),
                        href: meta.links.payments_group_select,
                    },
                    {
                        name: group.name,
                        href: null,
                    },
                ]"
            />
        </template>

        <div class="mt-3 flex justify-end space-x-1">
            <AppButton element="a" :href="meta.links.print">
                <svg viewBox="0 0 20 20" fill="currentColor" class="-ml-0.5 mr-2 w-4 h-4">
                    <path
                        fill-rule="evenodd"
                        d="M5 4v3H4a2 2 0 00-2 2v3a2 2 0 002 2h1v2a2 2 0 002 2h6a2 2 0 002-2v-2h1a2 2 0 002-2V9a2 2 0 00-2-2h-1V4a2 2 0 00-2-2H7a2 2 0 00-2 2zm8 0H7v3h6V4zm0 8H7v4h6v-4z"
                        clip-rule="evenodd"
                    ></path>
                </svg>
                {{ $t('messages.print') }}
            </AppButton>
            <AppButton element="InertiaLink" :href="meta.links.payments_general_index">
                {{ $t('messages.student_payment_list') }}
            </AppButton>
        </div>

        <div class="flex flex-col mt-5">
            <div class="-my-2 py-2 overflow-x-auto sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8">
                <div
                    class="align-middle inline-block min-w-full shadow overflow-hidden sm:rounded-lg border-b border-gray-200"
                >
                    <table class="min-w-full divide-y divide-gray-200">
                        <thead>
                            <tr>
                                <th
                                    class="max-w-xs px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                >
                                    {{ $t('messages.student') }}
                                </th>

                                <template v-for="column in columns" :key="column.id">
                                    <th
                                        class="max-w-xs px-6 py-3 bg-gray-50 text-right text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                    >
                                        {{ column.name }}
                                    </th>
                                    <th
                                        class="max-w-xs px-6 py-3 bg-gray-50 text-right text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                    >
                                        {{ $tc('messages.scholarships', 2) }}
                                    </th>
                                </template>

                                <th
                                    class="max-w-xs px-6 py-3 bg-gray-50 text-right text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                >
                                    <span class="sr-only">{{ $t('messages.actions') }}</span>
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr
                                :class="index % 2 === 0 ? 'bg-white' : 'bg-gray-50'"
                                v-for="(student, index) in students"
                                :key="student.id"
                            >
                                <td class="px-6 py-4 whitespace-nowrap text-sm leading-5 text-gray-500">
                                    <StudentInfoItem :student="student" />
                                </td>
                                <template v-for="column in student.columns" :key="column.id">
                                    <td
                                        class="px-6 py-4 whitespace-nowrap text-sm leading-5 font-mono text-right flex gap-x-2 items-center"
                                        :class="column.total ? 'text-gray-500' : 'text-gray-300 hover:text-gray-500'"
                                    >
                                        <div :title="column.message">
                                            <span v-if="column.quota">
                                                {{ $t('messages.X_of_Y', { X: column.total, Y: column.quota }) }}
                                            </span>
                                            <span v-else>{{ column.total }}</span>
                                            <div v-if="column.is_exempted">
                                                <BadgePill color="gray">
                                                    {{ $t('messages.exempt') }}
                                                </BadgePill>
                                            </div>
                                        </div>
                                        <div v-if="column.is_overpaid">
                                            <Tippy>
                                                <button>
                                                    <IconAlertCircleFilled class="w-6 h-6 text-yellow-400" />
                                                </button>
                                                <template #content>
                                                    {{ $t('messages.student_payment_type_is_overpaid') }}
                                                </template>
                                            </Tippy>
                                        </div>
                                    </td>
                                    <td class="px-2 py-4 whitespace-nowrap text-sm leading-5 text-gray-500">
                                        <div
                                            v-for="scholarship in column.scholarship_amounts"
                                            :key="scholarship.id"
                                            class="p-1"
                                        >
                                            <BadgePill color="indigo">
                                                {{ scholarship.name }}: {{ scholarship.amount }}
                                            </BadgePill>
                                        </div>
                                        &nbsp;
                                    </td>
                                </template>
                                <td
                                    class="px-6 py-4 whitespace-nowrap text-sm leading-5 text-gray-500 flex justify-end space-x-1"
                                >
                                    <InertiaSquareLinkButton
                                        action="show"
                                        :href="student.links.payments_index"
                                        v-if="student.links.payments_index"
                                    />
                                    <StudentPaymentCreateFormPanel
                                        v-if="student.can.create"
                                        :student="student"
                                        :url="student.links.payments_store"
                                        :selects="options.selects"
                                        square-button
                                        :scholarships="options.scholarships"
                                    />
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    </PanelPage>
</template>

<script>
import Layout from '@/Shared/Layout.vue';
import PanelPage from '@/Shared/PanelPage.vue';
import StudentPaymentCreateFormPanel from '@/components/StudentPaymentCreateFormPanel.vue';
import InertiaSquareLinkButton from '@/components/InertiaSquareLinkButton.vue';
import BadgePill from '@/components/BadgePill.vue';
import AppButton from '@/components/AppButton.vue';
import { IconAlertCircleFilled } from '@tabler/icons-vue';
import { Tippy } from 'vue-tippy';
import StudentInfoItem from '@/components/StudentInfoItem.vue';

export default {
    components: {
        StudentInfoItem,
        PanelPage,
        StudentPaymentCreateFormPanel,
        InertiaSquareLinkButton,
        BadgePill,
        AppButton,
        Tippy,
        IconAlertCircleFilled,
    },
    layout: Layout,
    name: 'Show',
    props: ['meta', 'group', 'options', 'columns', 'students'],
    data() {
        return {};
    },
    methods: {
        filter() {
            this.form.get(this.meta.links.index);
        },
    },
};
</script>
